import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { line as d3Line, curveMonotoneX } from "d3-shape";
import ChartHeader, { ToggleOption } from "./ChartHeader";
import ChartEmptyState from "./ChartEmptyState";
import ChartCardWrapper from "./ChartCardWrapper";
import ChartModal from "./ChartModal";
import ChartTooltipPortal from "./ChartTooltipPortal";

const getTickLabels = (
  allLabels: string[],
  isMobile: boolean,
): string[] | undefined => {
  const count = allLabels.length;

  let step: number | undefined;
  if (isMobile) {
    if (count <= 8) step = undefined;
    else if (count <= 16) step = 2;
    else if (count <= 24) step = 3;
    else step = 4;
  } else {
    if (count <= 12) step = undefined;
    else if (count <= 24) step = 2;
    else step = 3;
  }

  if (!step) return undefined;
  return allLabels.filter((_, i) => i % step === 0);
};

// Standard 1/2/5 tick generation so gridlines land on round values.
const niceTicks = (min: number, max: number, count: number): number[] => {
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  }
  const rawStep = (max - min) / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  let step = magnitude * 10;
  if (residual <= 1) step = magnitude;
  else if (residual <= 2) step = magnitude * 2;
  else if (residual <= 5) step = magnitude * 5;

  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step / 2; v += step) {
    // Snap away floating-point drift (0.30000000000000004 → 0.3)
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks;
};

// One period on the chart: a primary dot (e.g. the reported figure), an
// optional reference dot (e.g. the analyst estimate), and a subtle connector
// between them. Callers may attach extra fields for their tooltip content.
export type ScatterDatum = {
  period: string;
  value?: number | null;
  color?: string;
  refValue?: number | null;
  refColor?: string;
  [key: string]: unknown;
};

export interface GenericFinancialScatterChartProps {
  title: string;
  ticker?: string;
  height?: number;
  isModal?: boolean;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  onCloseModal?: () => void;

  isLoading: boolean;
  error: Error | null;

  chartData: ScatterDatum[];

  yAxisLegend?: string;
  yAxisFormat: (value: number) => string;

  tooltipContent?: (datum: ScatterDatum) => React.ReactNode;

  belowChartContent?: React.ReactNode;

  leftMargin?: number;
  // Draw a thin stem between the primary and reference dots (default true).
  // Even when off, the hovered column still shows its stem so wide gaps can
  // be paired on demand without permanent noise.
  showConnectors?: boolean;
  // Muted trend pair: a solid line through the primary values and a dashed
  // one through the reference values, making the gap between the two series
  // readable across time. Default false.
  showTrendLines?: boolean;

  noDataTitle?: string;
  noDataDescription?: string;

  headerToggleOptions?: ToggleOption[];
  headerToggleValue?: string;
  onHeaderToggleChange?: (value: string) => void;
  headerActions?: React.ReactNode;
  keepHeaderWhenEmpty?: boolean;
}

const GenericFinancialScatterChart: React.FC<GenericFinancialScatterChartProps> = (
  props
) => {
  const {
    title,
    height = 400,
    isModal = false,
    renderCard = true,
    showFullscreen = false,
    noHover = false,
    onCloseModal,
    isLoading,
    error,
    chartData,
    yAxisLegend,
    yAxisFormat,
    tooltipContent,
    belowChartContent,
    leftMargin,
    showConnectors = true,
    showTrendLines = false,
    noDataTitle,
    noDataDescription,
    headerToggleOptions,
    headerToggleValue,
    onHeaderToggleChange,
    headerActions,
    keepHeaderWhenEmpty = false,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // Both dimensions are measured: in modal mode the container is flexed to the
  // available space rather than the `height` prop, and an SVG drawn at the prop
  // height would overflow into whatever sits below the chart.
  const [measured, setMeasured] = useState({ width: 0, height: 0 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const isEmptyState =
    isLoading || !!error || !chartData || chartData.length === 0;

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setMeasured({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isEmptyState]);

  // Mobile-only: tap-toggled persistent tooltip, mirroring the bar/line generics.
  const [mobileTooltip, setMobileTooltip] = useState<{
    index: number;
    x: number;
    y: number;
  } | null>(null);
  const mobileTouchOriginRef = useRef<{ y: number } | null>(null);

  useEffect(() => {
    if (!isMobile || !mobileTooltip) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) mobileTouchOriginRef.current = { y: t.clientY };
      if (!chartContainerRef.current?.contains(e.target as Node)) {
        setMobileTooltip(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !mobileTouchOriginRef.current) return;
      if (Math.abs(t.clientY - mobileTouchOriginRef.current.y) > 10) {
        setMobileTooltip(null);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isMobile, mobileTooltip]);

  if (isEmptyState) {
    return (
      <ChartCardWrapper renderCard={renderCard}>
        {keepHeaderWhenEmpty && (
          <ChartHeader
            title={title}
            show={!noHover}
            toggleOptions={headerToggleOptions}
            toggleValue={headerToggleValue}
            onToggleChange={onHeaderToggleChange}
            actions={headerActions}
            showFullscreen={false}
            isModal={isModal}
            onCloseModal={onCloseModal}
          />
        )}
        <ChartEmptyState
          isLoading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && (!chartData || chartData.length === 0)}
          height={height}
          title={title}
          noDataTitle={noDataTitle}
          noDataDescription={noDataDescription}
        />
      </ChartCardWrapper>
    );
  }

  const margin = {
    top: isMobile ? 14 : 20,
    right: isMobile ? 10 : 20,
    bottom: isMobile ? 60 : 70,
    left: leftMargin ?? (isMobile ? 70 : 100),
  };

  // Draw at the measured size — the container tracks the `height` prop inline
  // but flexes to the available space in the fullscreen modal.
  const plotHeight = measured.height > 0 ? measured.height : height;
  const innerWidth = Math.max(0, measured.width - margin.left - margin.right);
  const innerHeight = Math.max(0, plotHeight - margin.top - margin.bottom);

  const values = chartData.flatMap((d) =>
    [d.value, d.refValue].filter(
      (v): v is number => typeof v === "number" && Number.isFinite(v)
    )
  );
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const pad = (dataMax - dataMin) * 0.08 || Math.abs(dataMax) * 0.1 || 1;
  const domainMin = dataMin - pad;
  const domainMax = dataMax + pad;

  const yScale = (v: number) =>
    margin.top +
    innerHeight -
    ((v - domainMin) / (domainMax - domainMin)) * innerHeight;

  const tickCount = isMobile ? 4 : 5;
  const yTicks = niceTicks(domainMin, domainMax, tickCount);

  const bandWidth = chartData.length > 0 ? innerWidth / chartData.length : 0;
  const bandCenter = (i: number) => margin.left + bandWidth * (i + 0.5);

  // Dense ranges shrink the dots so neighbouring columns stay distinct.
  const dotRadius = Math.max(3, Math.min(isMobile ? 5.5 : 7, bandWidth * 0.32));
  const refDotRadius = dotRadius * 0.8;

  const visibleTickLabels = (() => {
    if (isModal) return undefined;
    const allLabels = chartData.map((d) => d.period);
    return getTickLabels(allLabels, isMobile);
  })();
  const tickLabelVisible = (label: string) =>
    !visibleTickLabels || visibleTickLabels.includes(label);

  const axisText = {
    fontSize: isMobile ? 10 : 11,
    fontWeight: 600,
    fill: theme.palette.text.primary,
  };

  const fallbackColor = theme.palette.text.secondary;
  const hoverBandFill =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(0, 0, 0, 0.025)";

  const activeIndex = isMobile ? mobileTooltip?.index ?? null : hoveredIndex;

  const trendPaths = (() => {
    if (!showTrendLines || innerWidth <= 0) return null;

    const pathGen = d3Line<{ x: number; y: number }>()
      .x((p) => p.x)
      .y((p) => p.y)
      .curve(curveMonotoneX);

    const seriesPath = (accessor: (d: ScatterDatum) => number | null | undefined) => {
      const pts = chartData
        .map((d, i) => ({ x: bandCenter(i), v: accessor(d) }))
        .filter((p): p is { x: number; v: number } =>
          typeof p.v === "number" && Number.isFinite(p.v)
        )
        .map((p) => ({ x: p.x, y: yScale(p.v) }));
      return pts.length >= 2 ? pathGen(pts) : null;
    };

    return {
      values: seriesPath((d) => d.value),
      refs: seriesPath((d) => d.refValue),
    };
  })();

  const chartContent = (
    <>
      <Box sx={isModal ? { px: 2, pt: 2 } : undefined}>
        <ChartHeader
          title={title}
          show={!noHover}
          toggleOptions={headerToggleOptions}
          toggleValue={headerToggleValue}
          onToggleChange={onHeaderToggleChange}
          actions={headerActions}
          showFullscreen={showFullscreen}
          isModal={isModal}
          onOpenModal={() => setModalOpen(true)}
          onCloseModal={onCloseModal}
        />
      </Box>

      <Box
        ref={chartContainerRef}
        onMouseLeave={!isMobile ? () => setHoveredIndex(null) : undefined}
        sx={{
          height,
          ...(isModal && { flex: 1, minHeight: 0 }),
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.015)",
          borderRadius: 1,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {measured.width > 0 && (
          <svg width={measured.width} height={plotHeight} role="img" aria-label={title}>
            {/* Horizontal grid + y-axis tick labels */}
            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={margin.left}
                  x2={margin.left + innerWidth}
                  y1={yScale(tick)}
                  y2={yScale(tick)}
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                  strokeOpacity={0.2}
                />
                <text
                  x={margin.left - (isMobile ? 8 : 5)}
                  y={yScale(tick)}
                  textAnchor="end"
                  dominantBaseline="central"
                  style={axisText}
                >
                  {yAxisFormat(tick)}
                </text>
              </g>
            ))}

            {/* Vertical grid (desktop only, matching the bar/line generics) */}
            {!isMobile &&
              chartData.map((d, i) => (
                <line
                  key={`${d.period}-grid`}
                  x1={bandCenter(i)}
                  x2={bandCenter(i)}
                  y1={margin.top}
                  y2={margin.top + innerHeight}
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                  strokeOpacity={0.2}
                />
              ))}

            {/* Y-axis legend (desktop only) */}
            {!isMobile && yAxisLegend && (
              <text
                x={20}
                y={margin.top + innerHeight / 2}
                textAnchor="middle"
                transform={`rotate(-90, 20, ${margin.top + innerHeight / 2})`}
                style={{ ...axisText, fontSize: 12 }}
              >
                {yAxisLegend}
              </text>
            )}

            {/* X-axis tick labels */}
            {chartData.map((d, i) => {
              if (!tickLabelVisible(d.period)) return null;
              const x = bandCenter(i);
              const y = margin.top + innerHeight + (isMobile ? 14 : 20);
              return (
                <text
                  key={`${d.period}-label`}
                  x={x}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  transform={`rotate(-45, ${x}, ${y})`}
                  style={axisText}
                >
                  {d.period.replace(/ \(\d+\)$/, "")}
                </text>
              );
            })}

            {/* Hover band highlight */}
            {activeIndex !== null && chartData[activeIndex] && (
              <rect
                x={margin.left + bandWidth * activeIndex}
                y={margin.top}
                width={bandWidth}
                height={innerHeight}
                fill={hoverBandFill}
              />
            )}

            {/* Trend pair: solid through primary values, dashed through refs —
                the vertical gap between them is the series divergence */}
            {trendPaths?.refs && (
              <path
                d={trendPaths.refs}
                fill="none"
                stroke={fallbackColor}
                strokeWidth={1.5}
                strokeOpacity={0.22}
                strokeDasharray="4 5"
              />
            )}
            {trendPaths?.values && (
              <path
                d={trendPaths.values}
                fill="none"
                stroke={fallbackColor}
                strokeWidth={1.5}
                strokeOpacity={0.3}
              />
            )}

            {/* Connectors, reference dots, primary dots */}
            {chartData.map((d, i) => {
              const cx = bandCenter(i);
              const isActive = activeIndex === i;
              const hasValue =
                typeof d.value === "number" && Number.isFinite(d.value);
              const hasRef =
                typeof d.refValue === "number" && Number.isFinite(d.refValue);

              return (
                <g key={d.period}>
                  {(showConnectors || isActive) && hasValue && hasRef && (
                    <line
                      x1={cx}
                      x2={cx}
                      y1={yScale(d.refValue as number)}
                      y2={yScale(d.value as number)}
                      stroke={d.color ?? fallbackColor}
                      strokeWidth={1.5}
                      strokeOpacity={0.4}
                      style={{ transition: "y1 300ms ease, y2 300ms ease" }}
                    />
                  )}
                  {hasRef && (
                    // Hollow ring: expected values read as outlines, realized
                    // values as solid dots — distinct even in a shared colour.
                    <circle
                      cx={cx}
                      cy={yScale(d.refValue as number)}
                      r={isActive ? refDotRadius * 1.2 : refDotRadius}
                      fill={theme.palette.background.default}
                      stroke={d.refColor ?? fallbackColor}
                      strokeWidth={1.5}
                      style={{ transition: "cy 300ms ease, r 150ms ease" }}
                    />
                  )}
                  {hasValue && (
                    <circle
                      cx={cx}
                      cy={yScale(d.value as number)}
                      r={isActive ? dotRadius * 1.2 : dotRadius}
                      fill={d.color ?? fallbackColor}
                      style={{ transition: "cy 300ms ease, r 150ms ease" }}
                    />
                  )}
                </g>
              );
            })}

            {/* Per-band interaction targets */}
            {!noHover &&
              chartData.map((d, i) => (
                <rect
                  key={`${d.period}-hit`}
                  x={margin.left + bandWidth * i}
                  y={margin.top}
                  width={bandWidth}
                  height={innerHeight}
                  fill="transparent"
                  onMouseEnter={!isMobile ? () => setHoveredIndex(i) : undefined}
                  onClick={
                    isMobile
                      ? (e) => {
                          e.stopPropagation();
                          setMobileTooltip((prev) =>
                            prev?.index === i
                              ? null
                              : { index: i, x: e.clientX, y: e.clientY }
                          );
                        }
                      : undefined
                  }
                />
              ))}
          </svg>
        )}
      </Box>

      <Box sx={isModal ? { px: 2, pb: 1 } : undefined}>{belowChartContent}</Box>

      {!isMobile &&
        hoveredIndex !== null &&
        chartData[hoveredIndex] &&
        tooltipContent && (
          <ChartTooltipPortal
            anchorX={
              (chartContainerRef.current?.getBoundingClientRect().left ?? 0) +
              bandCenter(hoveredIndex)
            }
            containerRef={chartContainerRef}
          >
            {tooltipContent(chartData[hoveredIndex])}
          </ChartTooltipPortal>
        )}

      {isMobile &&
        mobileTooltip &&
        chartData[mobileTooltip.index] &&
        tooltipContent &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              left: Math.max(148, Math.min(mobileTooltip.x, window.innerWidth - 148)),
              top: mobileTooltip.y - 24,
              transform: "translate(-50%, -100%)",
              zIndex: 99999,
              pointerEvents: "none",
            }}
          >
            {tooltipContent(chartData[mobileTooltip.index])}
          </div>,
          document.body
        )}
    </>
  );

  return (
    <>
      <ChartCardWrapper renderCard={renderCard} isModal={isModal}>
        {chartContent}
      </ChartCardWrapper>

      {!isModal && (
        <ChartModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <GenericFinancialScatterChart
            {...props}
            isModal={true}
            renderCard={false}
            height={700}
            showFullscreen={false}
            onCloseModal={() => setModalOpen(false)}
          />
        </ChartModal>
      )}
    </>
  );
};

export default GenericFinancialScatterChart;
